import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { ViewLarge, Viewport, ViewSmall } from "bernie-view";

import { UitkHeading } from "uitk-react-text";
import { UitkCarousel } from "uitk-react-carousel";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { AnalyticsConfig } from "uitk-react-utils";

import VisitorBlogCard from "./components/VisitorBlogCard";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { VisitorBlogsProps } from "./typings";
import { Article as VisitorBlog } from "typings/microserviceModels/te-article-flex-module";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { withStores } from "stores";

const VisitorBlogs = withStores(
  "analytics",
  "thumbor"
)((props: VisitorBlogsProps) => {
  const { flexComponent, thumbor, context } = props;
  const { model } = flexComponent;
  const { content, title } = model;

  if (!content?.items?.length) {
    return null;
  }

  const { items: visitorBlogs } = content;
  const { name: locationName } = context.searchContext.location;
  const { formatText } = useLocalization();

  const articleKeyHelper = new ItemKeyHelper("top-articles");
  const localisedHeading = title || formatText("visitorBlogs.title", locationName);

  const safelyResizeImage = (visitorBlog: VisitorBlog) => {
    const image = visitorBlog.media?.mediaUrl;

    return (
      image ?? thumbor.safelyGenerateThumborizedImageUrl(image, 600, 300, context, AspectRatioEnum.DEFAULT, true, 100)
    );
  };

  const analytics: AnalyticsConfig = {
    id: "TG.LP.Dest.Articles",
    callback: (id, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: id,
        action: Action.SCROLL,
        linkName: description
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    }
  };

  return (
    <Viewport>
      <ViewSmall>
        <div
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
          className={`VisitorBlogs${visitorBlogs.length > 1 ? " VisitorBlogs-carousel" : ""}`}
        >
          <UitkCarousel
            headline={localisedHeading}
            analytics={analytics}
            carouselName="Visitor Blogs"
            itemsVisible={{ lg: 1, md: 1, sm: 1 }}
            pageBy={1}
            peekWidth={24}
            buttonText={{
              nextButton: formatText("carousel.item.next"),
              prevButton: formatText("carousel.item.prev")
            }}
          >
            {visitorBlogs.map((visitorBlog: VisitorBlog) => (
              <VisitorBlogCard
                key={articleKeyHelper.next()}
                visitorBlog={visitorBlog}
                visitorBlogImageUrl={safelyResizeImage(visitorBlog)}
              />
            ))}
          </UitkCarousel>
        </div>
      </ViewSmall>

      <ViewLarge>
        <div
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
          className="VisitorBlogs uitk-cell all-cell-1-1"
        >
          <UitkHeading level={3} size={600}>
            {localisedHeading}
          </UitkHeading>

          <UitkLayoutGrid className="all-t-margin-three" columns={3} space="three">
            {visitorBlogs.map((visitorBlog: VisitorBlog) => (
              <VisitorBlogCard
                key={articleKeyHelper.next()}
                visitorBlog={visitorBlog}
                visitorBlogImageUrl={safelyResizeImage(visitorBlog)}
              />
            ))}
          </UitkLayoutGrid>
        </div>
      </ViewLarge>
    </Viewport>
  );
});

export default VisitorBlogs;
